/**
 * Web (browser) partner attribution.
 *
 * Traffic that lands directly on the web app via a partner link
 * (`/?ref=<key>&sub=<subid>` or `/partner?ref=…`) never passes through a
 * Telegram bot, so the bot's attribution/postback machinery never sees it. These
 * helpers report the equivalent conversions to the backend (`/api/track/*`),
 * which records them into the SAME per-SubID ledger the bot uses — so web
 * traffic shows up in the partner dashboard exactly like bot traffic.
 *
 * A stable per-browser `click_id` (persisted in localStorage) identifies the
 * visitor across the landing → wallet-connect → on-chain-buy funnel. Everything
 * here is best-effort and only runs on a genuine browser visit with the caching
 * backend configured — inside Telegram the bot already owns attribution, so we
 * stay out of its way.
 */
import { getClickId } from './attribution';
import { API_BASE, USE_PROXY } from './config';
import {
  getIncomingClickId,
  getIncomingReferrerKey,
  getIncomingSubId,
  isTelegramMiniApp,
} from './referral';

/** Whether web attribution should run for this visit (browser + proxy backend). */
function webTrackingEnabled(): boolean {
  return USE_PROXY && !isTelegramMiniApp();
}

async function post(path: string, body: Record<string, unknown>): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/track/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    /* best-effort: never disrupt the app */
  }
}

let clickReported = false;

/**
 * Records the web landing (attributes the visitor to the referrer's campaign +
 * SubID and fires `app_open`). Fires at most once per load and only when a ref
 * key is present. No-op inside Telegram / without the proxy backend.
 */
export async function reportWebClick(): Promise<void> {
  if (!webTrackingEnabled() || clickReported) return;
  const ref = getIncomingReferrerKey();
  if (ref === null) return;
  clickReported = true;
  await post('click', {
    clickId: getClickId(),
    ref: String(ref),
    sub: getIncomingSubId() ?? 'main',
    cid: getIncomingClickId(),
  });
}

/**
 * Links the connected wallet to this visitor (so the backend indexer can later
 * fire the on-chain `ticket_purchase` conversion) and fires `wallet_auth`.
 * No-op inside Telegram / without the proxy backend / without an address.
 */
export async function reportWebAuth(address: string): Promise<void> {
  if (!webTrackingEnabled() || !address) return;
  await post('auth', { clickId: getClickId(), address });
}
